import type { LocalAabb } from './aabb';
import { assertLocalAabb } from './aabb';
import type { Vec2 } from './vec2';
import { assertFiniteNumber, assertFiniteVec2 } from './vec2';

export interface StageBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly groundY: number;
}

export interface MovementData {
  readonly forwardSpeed: number;
  readonly backwardSpeed: number;
  readonly airControlSpeed: number;
  readonly jumpVelocity: number;
  readonly gravity: number;
}

export interface CharacterSpatialDefinition {
  readonly pushBox: LocalAabb;
  readonly movement: MovementData;
}

export interface TrapSpatialState {
  readonly instanceId: string;
  readonly ownerId: string;
  position: { x: number; y: number };
  readonly spawnFacing: 'LEFT' | 'RIGHT';
}

export const DEFAULT_STAGE: StageBounds = Object.freeze({ minX: 0, maxX: 1280, groundY: 620 });
export const DEFAULT_SPATIAL_DEFINITION: CharacterSpatialDefinition = Object.freeze({
  pushBox: Object.freeze({ minX: -32, minY: -120, maxX: 32, maxY: 0 }),
  movement: Object.freeze({
    forwardSpeed: 5,
    backwardSpeed: 4,
    airControlSpeed: 3,
    jumpVelocity: -16,
    gravity: 1,
  }),
});

export const assertStageBounds = (stage: StageBounds): void => {
  assertFiniteNumber(stage.minX, 'stage.minX');
  assertFiniteNumber(stage.maxX, 'stage.maxX');
  assertFiniteNumber(stage.groundY, 'stage.groundY');
  if (stage.maxX <= stage.minX) throw new RangeError('stage.maxX must be greater than stage.minX.');
};

export const assertSpatialDefinition = (definition: CharacterSpatialDefinition, name = 'spatial'): void => {
  assertLocalAabb(definition.pushBox, `${name}.pushBox`);
  const movement = definition.movement;
  assertFiniteNumber(movement.forwardSpeed, `${name}.movement.forwardSpeed`);
  assertFiniteNumber(movement.backwardSpeed, `${name}.movement.backwardSpeed`);
  assertFiniteNumber(movement.airControlSpeed, `${name}.movement.airControlSpeed`);
  assertFiniteNumber(movement.jumpVelocity, `${name}.movement.jumpVelocity`);
  assertFiniteNumber(movement.gravity, `${name}.movement.gravity`);
  if (movement.forwardSpeed < 0 || movement.backwardSpeed < 0 || movement.airControlSpeed < 0) {
    throw new RangeError(`${name} movement speeds must be non-negative.`);
  }
  if (movement.jumpVelocity >= 0) throw new RangeError(`${name}.movement.jumpVelocity must be negative.`);
  if (movement.gravity < 0) throw new RangeError(`${name}.movement.gravity must be non-negative.`);
};

export const createTrapSpatialState = (
  instanceId: string,
  ownerId: string,
  position: Vec2,
  spawnFacing: 'LEFT' | 'RIGHT',
  stage: StageBounds,
): TrapSpatialState => {
  if (instanceId.length === 0 || ownerId.length === 0) throw new RangeError('Trap ids must not be empty.');
  assertFiniteVec2(position, 'trap.position');
  assertStageBounds(stage);
  return {
    instanceId,
    ownerId,
    position: {
      x: Math.min(stage.maxX, Math.max(stage.minX, position.x)),
      y: Math.min(stage.groundY, position.y),
    },
    spawnFacing,
  };
};

export const cloneTrapSpatialState = (trap: Readonly<TrapSpatialState>): TrapSpatialState => ({
  instanceId: trap.instanceId,
  ownerId: trap.ownerId,
  position: { ...trap.position },
  spawnFacing: trap.spawnFacing,
});
